import * as Sentry from '@sentry/react';
import { Primitive } from 'type-fest';
import env from '~/env';

type CaptureContext = {
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
  tags?: Record<string, string>;
};

export function logError(error: Error, context?: CaptureContext) {
  if (env.VITE_NODE_ENV === 'dev') {
    console.error('Error logged:\n', error, '\nContext:\n', context);
  }
  Sentry.captureException(error, context);
}

export function logMessage(
  message: string,
  context?: CaptureContext | Sentry.SeverityLevel,
) {
  if (env.VITE_NODE_ENV === 'dev') {
    // oxlint-disable-next-line no-console -- only applies in non-production environments
    console.info('Message logged:\n', message, '\nContext:\n', context);
  }
  Sentry.captureMessage(message, context);
}

export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (env.VITE_NODE_ENV === 'dev') {
    // oxlint-disable-next-line no-console -- only applies in non-production environments
    console.info('Breadcrumb added:\n', breadcrumb);
  }
  Sentry.addBreadcrumb(breadcrumb);
}

type IdentifyUserParams = {
  email: string;
  storeName: string;
  storeId: number;
  orderNo: string;
  orderIdFromPlatform: string;
  [key: string]: Primitive;
};

export function identifyUser(params: IdentifyUserParams) {
  try {
    if (env.VITE_NODE_ENV === 'prod') {
      const { email, ...tags } = params;

      Sentry.setUser({
        email,
        username: email,
      });

      Object.entries(tags).forEach(([key, value]) => {
        Sentry.setTag(key, value);
      });
    }
  } catch (error) {
    logError(
      error instanceof Error
        ? error
        : new Error('Failed to identify user', { cause: error }),
    );
  }
}

// ? storeIds we want to capture all replays for, regardless of sample rate
const captureAllReplayStoreIds: number[] = [];

// flush() covers every state: starts a session replay if replay is off, sends
// the error buffer as a replay, or queues a flush if already recording.
export function forceReplay(storeId: number) {
  if (env.VITE_NODE_ENV !== 'prod') return;
  if (!captureAllReplayStoreIds.includes(storeId)) return;
  Sentry.getReplay()
    ?.flush()
    .catch((error) => {
      logError(
        error instanceof Error
          ? error
          : new Error('Failed to force replay', { cause: error }),
      );
    });
}
